/**
 * Feature 1: Sales Funnel Visualization
 * Shows view -> cart -> purchase conversion funnel
 */

import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { Card, CardContent, Typography, CircularProgress, Alert } from '@mui/material';
import apiService from '../services/apiService';

const COLORS = ['#4CAF50', '#FF9800', '#F44336'];

const SalesFunnel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getSalesFunnel();
      setData(response.funnel);
    } catch (err) {
      setError('Failed to load sales funnel data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const calculateConversionRate = (current, previous) => {
    if (!previous) return 100;
    return ((current / previous) * 100).toFixed(2);
  };

  if (loading) {
    return (
      <Card>
        <CardContent style={{ textAlign: 'center', padding: '40px' }}>
          <CircularProgress />
          <Typography variant="body2" style={{ marginTop: '16px' }}>
            Loading sales funnel...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardContent>
          <Alert severity="info">No data available</Alert>
        </CardContent>
      </Card>
    );
  }

  // Calculate conversion rates
  const enrichedData = data.map((item, index) => ({
    ...item,
    conversion_rate: index > 0 ? calculateConversionRate(item.event_count, data[index - 1].event_count) : 100,
  }));

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Sales Funnel Visualization
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          Customer journey from view to purchase
        </Typography>

        {/* Summary Stats */}
        <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', flexWrap: 'wrap' }}>
          {enrichedData.map((stage, index) => (
            <div key={stage.stage} style={{ flex: '1', minWidth: '200px' }}>
              <Typography variant="caption" color="textSecondary">
                {stage.stage.toUpperCase()}
              </Typography>
              <Typography variant="h6">
                {formatNumber(stage.event_count)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {formatNumber(stage.unique_users)} users
              </Typography>
              {index > 0 && (
                <Typography variant="body2" style={{ color: COLORS[index] }}>
                  {stage.conversion_rate}% from previous
                </Typography>
              )}
            </div>
          ))}
        </div>

        {/* Funnel Chart */}
        <ResponsiveContainer width="100%" height={400}>
          <BarChart
            data={enrichedData}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="stage" />
            <YAxis 
              tickFormatter={(value) => {
                if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                return value;
              }}
            />
            <Tooltip 
              formatter={(value) => formatNumber(value)}
              labelStyle={{ color: '#000' }}
            />
            <Legend />
            <Bar dataKey="event_count" name="Total Events" radius={[8, 8, 0, 0]}>
              {enrichedData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
            <Bar dataKey="unique_users" name="Unique Users" fill="#82ca9d" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>

        {/* Conversion Summary */}
        <div style={{ marginTop: '20px', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
          <Typography variant="subtitle2" gutterBottom>
            Overall Conversion Metrics
          </Typography>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <div>
              <Typography variant="caption">View to Cart</Typography>
              <Typography variant="body1">
                {calculateConversionRate(data[1].event_count, data[0].event_count)}%
              </Typography>
            </div>
            <div>
              <Typography variant="caption">Cart to Purchase</Typography>
              <Typography variant="body1">
                {calculateConversionRate(data[2].event_count, data[1].event_count)}%
              </Typography>
            </div>
            <div>
              <Typography variant="caption">View to Purchase</Typography>
              <Typography variant="body1">
                {calculateConversionRate(data[2].event_count, data[0].event_count)}%
              </Typography>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SalesFunnel;
